'use client';

import type React from 'react';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  TrophyIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LogOutIcon,
  XIcon,
  HomeIcon as HouseIcon,
  WalletIcon,
  UserIcon,
  HelpCircleIcon,
  TvIcon,
  LayoutDashboard,
  MedalIcon,
  ShieldIcon,
  UsersIcon,
  ScrollTextIcon,
  PlayCircleIcon,
  SmartphoneIcon,
  BuildingIcon,
  MailIcon,
  Receipt as ReceiptIcon,
  CalendarClock,
  TrendingUp as TrendingUpIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/contexts/SidebarContext';
import { useTournamentStore } from '@/store/useTournamentStore';
import {
  ACTIVE_TOURNAMENT_STATUSES,
  isTournamentActive,
} from '@/utils/tournament-status';
import { useMercadoPagoAuth } from '@/hooks/useMercadoPagoAuth';
import { useAuth } from '@/hooks/useAuth';
import { MercadoPagoAuthModal } from '../mercadopago/MercadoPagoAuthModal';
import ConfirmLogoutModal from '../common/ConfirmLogoutModal';

/**
 * The backoffice section of the sidebar. Data rather than JSX because the two
 * navs below render the same links, and future backoffice phases will keep
 * adding more — writing each one twice is how the mobile and desktop trees
 * drift apart.
 */
const BACKOFFICE_NAV_ITEMS = [
  { to: '/admin/usuarios', label: 'Usuarios', Icon: UsersIcon },
  { to: '/admin/torneos', label: 'Torneos', Icon: TrophyIcon },
  { to: '/admin/deuda', label: 'Deuda', Icon: WalletIcon },
  { to: '/admin/deuda/transferencias', label: 'Transferencias', Icon: ReceiptIcon },
  { to: '/admin/auditoria', label: 'Auditoría', Icon: ScrollTextIcon },
  { to: '/admin/operaciones/jobs', label: 'Jobs', Icon: PlayCircleIcon },
  { to: '/admin/operaciones/versiones', label: 'Versiones', Icon: SmartphoneIcon },
  { to: '/admin/operaciones/clubs', label: 'Clubes', Icon: BuildingIcon },
  { to: '/admin/operaciones/emails', label: 'Emails', Icon: MailIcon },
  { to: '/admin/metricas', label: 'Métricas', Icon: TrendingUpIcon },
];

const Sidebar: React.FC<{ mobile?: boolean }> = ({ mobile = false }) => {
  const { isCollapsed, toggleDesktop, toggleMobile } = useSidebar();
  const [tournamentsExpanded, setTournamentsExpanded] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const location = useLocation();
  const {
    clearTokens,
    isPureInstructor,
    isFutureOwner,
    isPendingDebtOnly,
    isPureAdmin,
    hasAdminRole,
    isUserReady,
  } = useAuth();
  const {
    isModalOpen,
    setIsModalOpen,
    handleNavigateNewTournament,
    handleAuthorizeMercadoPago,
  } = useMercadoPagoAuth();

  // Get tournaments from the global store
  const { tournaments, fetchTournaments, isInitialized, clearTournaments } =
    useTournamentStore();

  // Load tournaments when component mounts (only if not already initialized)
  useEffect(() => {
    // Pure instructors, PENDING_DEBT-only, and pure-admin users don't manage tournaments: skip the fetch.
    // Gated on isUserReady because every flag below is derived from user.roles, which is null on
    // the first render: without it a pure admin fired an owner-only fetch, got a 403, and the API
    // interceptor read that as an expired session and logged them out.
    if (
      isUserReady &&
      !isInitialized &&
      !isPureInstructor &&
      !isPendingDebtOnly &&
      !isFutureOwner &&
      !isPureAdmin
    ) {
      fetchTournaments();
    }
  }, [
    isUserReady,
    isInitialized,
    fetchTournaments,
    isPureInstructor,
    isPendingDebtOnly,
    isFutureOwner,
    isPureAdmin,
  ]);


  // Sidebar list: drop FINISHED/CANCELLED, keep the ACTIVE_TOURNAMENT_STATUSES
  // order (in progress before upcoming), then closest start date first.
  const activeTournaments = tournaments.filter(isTournamentActive).sort((a, b) => {
    const orderDiff =
      ACTIVE_TOURNAMENT_STATUSES.indexOf(a.status) -
      ACTIVE_TOURNAMENT_STATUSES.indexOf(b.status);
    return orderDiff !== 0
      ? orderDiff
      : Date.parse(a.startDate) - Date.parse(b.startDate);
  });

  const sidebarTournaments = activeTournaments.slice(0, 5);
  const hasMoreTournaments = activeTournaments.length > 5;

  const isActive = (path: string) => location.pathname === path;
  const handleToggle = mobile ? toggleMobile : toggleDesktop;

  // The mobile tree returns early with `mobile` true, so the collapse branches
  // below resolve to the mobile variant there and to the desktop one here.
  const renderBackofficeNav = () => (
    <>
      <div
        className={`mt-4 flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400 ${isCollapsed && !mobile ? 'justify-center' : ''}`}
      >
        <ShieldIcon className="h-3.5 w-3.5" />
        {(!isCollapsed || mobile) && <span>Backoffice</span>}
      </div>
      {BACKOFFICE_NAV_ITEMS.map(({ to, label, Icon }) => (
        <Link
          key={to}
          to={to}
          className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
            isActive(to)
              ? 'bg-blue-100 text-blue-700'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          } ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
          onClick={mobile ? handleToggle : undefined}
        >
          <Icon className="h-4 w-4" />
          {(!isCollapsed || mobile) && <span>{label}</span>}
        </Link>
      ))}
    </>
  );

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    clearTokens();
    clearTournaments(); // Clear tournament data when logging out
    setShowLogoutModal(false);
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  // If mobile, render only the content without the aside
  if (mobile) {
    return (
      <>
        <div className="flex h-full flex-col">
          {/* Close button in mobile sidebar */}
          <div className="flex h-16 items-center justify-start border-b border-gray-200 bg-white px-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggle}
              className="text-gray-600 hover:text-gray-900"
            >
              <XIcon className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col bg-gray-50">
            <nav className="flex-1 overflow-y-auto px-2 py-4">
              {!isPureAdmin && (
                <>
              {!isPendingDebtOnly && !isFutureOwner && (
                <Link
                  to="/inicio"
                  className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive('/inicio')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  } space-x-3`}
                  onClick={handleToggle}
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span>Inicio</span>
                </Link>
              )}
              {!isPendingDebtOnly && (
                <Link
                  to="/clubs"
                  className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive('/clubs')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  } space-x-3`}
                  onClick={handleToggle}
                >
                  <HouseIcon className="h-4 w-4" />
                  <span>Mis Clubs</span>
                </Link>
              )}
              {!isPendingDebtOnly && !isFutureOwner && (
                <Link
                  to="/turnos"
                  className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive('/turnos')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  } space-x-3`}
                  onClick={handleToggle}
                >
                  <CalendarClock className="h-4 w-4" />
                  <span>Turnos</span>
                </Link>
              )}
              {!isPendingDebtOnly && !isFutureOwner && (
                <Link
                  to="/rankings"
                  className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive('/rankings')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  } space-x-3`}
                  onClick={handleToggle}
                >
                  <MedalIcon className="h-4 w-4" />
                  <span>Rankings</span>
                </Link>
              )}

              {!isPureInstructor && !isPendingDebtOnly && !isFutureOwner && (
                <>
              <div className="mt-2">
                <Link
                  to="/"
                  className={`flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive('/') || location.pathname.startsWith('/torneos/')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  onClick={handleToggle}
                >
                  <TrophyIcon className="h-4 w-4" />
                  <span className="ml-3">Mis Torneos</span>
                  {sidebarTournaments.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTournamentsExpanded(!tournamentsExpanded);
                      }}
                      className="ml-auto"
                    >
                      <ChevronUpIcon
                        className={`h-4 w-4 transition-transform ${
                          tournamentsExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  )}
                </Link>
                {tournamentsExpanded && sidebarTournaments.length > 0 && (
                  <div className="ml-6 mt-1 space-y-1">
                    {sidebarTournaments.map((tournament) => (
                      <Link
                        key={tournament.id}
                        to={`/torneos/${tournament.id}`}
                        className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                          isActive(`/torneos/${tournament.id}`)
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        onClick={handleToggle}
                      >
                        <span className="truncate">{tournament.name}</span>
                      </Link>
                    ))}
                    {hasMoreTournaments && (
                      <Link
                        to="/"
                        className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                        onClick={handleToggle}
                      >
                        <span>Ver todos</span>
                      </Link>
                    )}
                  </div>
                )}
              </div>

              <Link
                to="/crear-torneo"
                className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/crear-torneo')
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } space-x-3`}
                onClick={(e) => {
                  handleToggle();
                  handleNavigateNewTournament(e);
                }}
              >
                <PlusIcon className="h-4 w-4" />
                <span>Nuevo Torneo</span>
              </Link>
                </>
              )}

              <Link
                to="/cuenta-corriente"
                className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/cuenta-corriente')
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } space-x-3`}
                onClick={handleToggle}
              >
                <WalletIcon className="h-4 w-4" />
                <span>Cuenta Corriente</span>
              </Link>
              {!isPureInstructor && !isPendingDebtOnly && !isFutureOwner && (
                <Link
                  to="/tv"
                  className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive('/tv')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  } space-x-3`}
                  onClick={handleToggle}
                >
                  <TvIcon className="h-4 w-4" />
                  <span>Modo TV</span>
                </Link>
              )}
              {!isPendingDebtOnly && (
                <Link
                  to="/profile"
                  className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive('/profile')
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  } space-x-3`}
                  onClick={handleToggle}
                >
                  <UserIcon className="h-5 w-5" />
                  {(!isCollapsed || mobile) && <span>Mi Perfil</span>}
                </Link>
              )}
              {!isPendingDebtOnly && (
                <a
                  href="http://getmatchapp.com/ayuda"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900 space-x-3"
                  onClick={handleToggle}
                >
                  <HelpCircleIcon className="h-5 w-5" />
                  {(!isCollapsed || mobile) && <span>Ayuda</span>}
                </a>
              )}
                </>
              )}
              {hasAdminRole && renderBackofficeNav()}
            </nav>

            <div className="border-t border-gray-200 bg-gray-50 px-2 py-4">
              <button
                onClick={handleLogoutClick}
                className="flex w-full items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-red-50 hover:text-red-600"
              >
                <LogOutIcon className="h-4 w-4" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>

        <MercadoPagoAuthModal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          onAuthorizeMercadoPago={handleAuthorizeMercadoPago}
        />

        <ConfirmLogoutModal
          isOpen={showLogoutModal}
          onClose={handleCancelLogout}
          onConfirm={handleConfirmLogout}
        />
      </>
    );
  }

  return (
    <>
      <aside
        className={`fixed left-0 top-16 z-10 h-[calc(100vh-4rem)] max-h-[calc(100vh-4rem)] border-r border-gray-200 bg-gray-50 ${
          isCollapsed ? 'w-16' : 'w-56'
        } hidden transition-all duration-300 lg:block`}
      >
        <div className="flex h-full max-h-[calc(100vh-4rem)] flex-col">
          <nav className="flex-1 overflow-y-auto px-2 py-4">
            {!isPureAdmin && (
              <>
            {!isPendingDebtOnly && !isFutureOwner && (
              <Link
                to="/inicio"
                className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/inicio')
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
                onClick={mobile ? handleToggle : undefined}
              >
                <LayoutDashboard className="h-4 w-4" />
                {(!isCollapsed || mobile) && <span>Inicio</span>}
              </Link>
            )}
            {!isPendingDebtOnly && (
              <Link
                to="/clubs"
                className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/clubs')
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
                onClick={mobile ? handleToggle : undefined}
              >
                <HouseIcon className="h-4 w-4" />
                {(!isCollapsed || mobile) && <span>Mis Clubs</span>}
              </Link>
            )}
            {!isPendingDebtOnly && !isFutureOwner && (
              <Link
                to="/turnos"
                className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/turnos')
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
                onClick={mobile ? handleToggle : undefined}
              >
                <CalendarClock className="h-4 w-4" />
                {(!isCollapsed || mobile) && <span>Turnos</span>}
              </Link>
            )}
            {!isPendingDebtOnly && !isFutureOwner && (
              <Link
                to="/rankings"
                className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/rankings')
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
                onClick={mobile ? handleToggle : undefined}
              >
                <MedalIcon className="h-4 w-4" />
                {(!isCollapsed || mobile) && <span>Rankings</span>}
              </Link>
            )}
            {!isPureInstructor && !isPendingDebtOnly && !isFutureOwner && (
              <>
            <div
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive('/')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              } ${isCollapsed && !mobile ? 'justify-center' : 'justify-between'}`}
            >
              <Link
                to="/"
                className={`flex items-center ${isCollapsed && !mobile ? 'justify-center' : 'flex-grow space-x-3'}`}
                onClick={mobile ? handleToggle : undefined}
              >
                <TrophyIcon className="h-4 w-4" />
                {(!isCollapsed || mobile) && <span>Mis Torneos</span>}
              </Link>
              {(!isCollapsed || mobile) && sidebarTournaments.length > 0 && (
                <button
                  onClick={() => setTournamentsExpanded(!tournamentsExpanded)}
                  className="ml-auto rounded p-1 hover:bg-gray-200"
                  aria-label={
                    tournamentsExpanded
                      ? 'Colapsar lista de torneos'
                      : 'Expandir lista de torneos'
                  }
                >
                  <ChevronDownIcon
                    className={`h-4 w-4 transform transition-transform ${tournamentsExpanded ? 'rotate-180' : ''}`}
                  />
                </button>
              )}
            </div>
            {(!isCollapsed || mobile) && tournamentsExpanded && sidebarTournaments.length > 0 && (
              <div className="mt-2 space-y-1 pl-4">
                {sidebarTournaments.map((tournament) => (
                  <Link
                    key={tournament.id}
                    to={`/torneos/${tournament.id}`}
                    className={`block rounded px-3 py-2 text-sm transition-colors ${
                      isActive(`/torneos/${tournament.id}`)
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={mobile ? handleToggle : undefined}
                    title={tournament.name}
                  >
                    <div className="max-w-full overflow-hidden truncate text-ellipsis whitespace-nowrap">
                      {tournament.name}
                    </div>
                  </Link>
                ))}
                {hasMoreTournaments && (
                  <Link
                    to="/"
                    className="block rounded px-3 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-gray-100 hover:text-blue-700"
                    onClick={mobile ? handleToggle : undefined}
                  >
                    Ver todos
                  </Link>
                )}
              </div>
            )}
            <Link
              to="/crear-torneo"
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive('/crear-torneo')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              } ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
              onClick={(e) => {
                if (mobile) handleToggle();
                handleNavigateNewTournament(e);
              }}
            >
              <PlusIcon className="h-4 w-4" />
              {(!isCollapsed || mobile) && <span>Nuevo Torneo</span>}
            </Link>
              </>
            )}

            <Link
              to="/cuenta-corriente"
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isActive('/cuenta-corriente')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              } ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
              onClick={mobile ? handleToggle : undefined}
            >
              <WalletIcon className="h-4 w-4" />
              {(!isCollapsed || mobile) && <span>Cuenta Corriente</span>}
            </Link>
            {!isPureInstructor && !isPendingDebtOnly && !isFutureOwner && (
              <Link
                to="/tv"
                className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/tv')
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
                onClick={mobile ? handleToggle : undefined}
              >
                <TvIcon className="h-4 w-4" />
                {(!isCollapsed || mobile) && <span>Modo TV</span>}
              </Link>
            )}
            {!isPendingDebtOnly && (
              <Link
                to="/profile"
                className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/profile')
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
                onClick={mobile ? handleToggle : undefined}
              >
                <UserIcon className="h-4 w-4" />
                {(!isCollapsed || mobile) && <span>Mi Perfil</span>}
              </Link>
            )}
            {!isPendingDebtOnly && (
              <a
                href="http://getmatchapp.com/ayuda"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900 ${isCollapsed && !mobile ? 'justify-center' : 'space-x-3'}`}
                onClick={mobile ? handleToggle : undefined}
              >
                <HelpCircleIcon className="h-4 w-4" />
                {(!isCollapsed || mobile) && <span>Ayuda</span>}
              </a>
            )}
              </>
            )}
            {hasAdminRole && renderBackofficeNav()}
          </nav>

          {/* Logout button - fixed at bottom */}
          <div className="border-t border-gray-200 bg-gray-50 px-2 py-4">
            <button
              onClick={handleLogoutClick}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-red-50 hover:text-red-600 ${
                isCollapsed && !mobile ? 'justify-center' : 'space-x-3'
              }`}
            >
              <LogOutIcon className="h-4 w-4" />
              {(!isCollapsed || mobile) && <span>Cerrar Sesión</span>}
            </button>
          </div>
        </div>
      </aside>

      <MercadoPagoAuthModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onAuthorizeMercadoPago={handleAuthorizeMercadoPago}
      />

      <ConfirmLogoutModal
        isOpen={showLogoutModal}
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
};

export default Sidebar;
